import React, { lazy, Suspense, useEffect, useState } from 'react';
import './ford-rides-home.scss';
import notificationIcon from '../../../../assets/ford-rides/ford-rides-home-notifications-icon.svg';
import close from '../../../../assets/ford-rides/close.svg';
import exportIcon from '../../../../assets/ford-rides/export.svg';
import filterIcon from '../../../../assets/ford-rides/Filter.svg';
import FordRidesAccordions from '../ford-rides-accordions/ford-rides-accordions';
import { PrimaryButton, SecondaryButton } from '../../../common';
import useFordRidesContext from '../hooks/use-ford-rides-context';
import { ReducerInitialStates } from '../../../../contexts/ford-rides-context/ford-rides-context';
import {
    ComponentType,
    RideType,
} from '../../../../views/ford-rides-view/ford-rides-view';
import { ReducerInitialState } from '../../../../models/ford-rides-trips-service-model';
import RidesExport from '../rides-export/rides-export';
import { FordRidesContentProps } from '../hooks/use-ford-rides-content';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { displayUtilityType } from '../all-rides/all-rides';
import { ActivityIndicator } from '../../../common/activity-indicator/activity-indicator';

const RidesDistanceDurationFilter = lazy(() =>
    import(
        '../rides-filter/rides-distance-duration-filter/rides-distance-duration-filter'
    )
);
export interface UtilityButtonsProps {
    getFilterCount: () => number;
    handleUtility: (event: React.MouseEvent<HTMLButtonElement>) => void;
    filterIcon: string;
    exportIcon: string;
    state: ReducerInitialStates;
}

export const UtilityButtons: React.FC<UtilityButtonsProps> = ({
    getFilterCount,
    handleUtility,
    filterIcon,
    exportIcon,
    state,
}) => {
    const isRidesPresent = Object.keys(state).some(stateKey => {
        if (
            (state[
                stateKey as keyof ReducerInitialStates
            ] as ReducerInitialState[]).length
        ) {
            return true;
        } else {
            return false;
        }
    });
    return (
        <div className="utility-buttons">
            {isRidesPresent ? (
                <button
                    data-count={getFilterCount()}
                    aria-label={'Filter' as displayUtilityType}
                    className="filter-button"
                    onClick={handleUtility}
                >
                    <img src={filterIcon} alt="Filter" />
                </button>
            ) : null}
            <button
                className="export-button"
                aria-label={'Export' as displayUtilityType}
                onClick={handleUtility}
            >
                <img src={exportIcon} alt="Export" />
            </button>
        </div>
    );
};

interface Props {
    rideType?: RideType[];
    setRideType: (data: RideType[]) => void;
    setView: (data: ComponentType) => void;
    fordRidesData: FordRidesContentProps;
}

function FordRidesHome({ fordRidesData, ...props }: Props) {
    const {
        FordRidesDateRangePicker,
        state,
        apiStatus,
        distanceDurationFilter,
    } = useFordRidesContext();
    const [showNotification, setShowNotification] = useState<boolean>(true);
    const handleSetRideType = () => {
        const newState = Object.keys(state).filter(stateKey => {
            if (
                (state[
                    stateKey as keyof ReducerInitialStates
                ] as ReducerInitialState[]).length
            ) {
                return true;
            } else {
                return false;
            }
        });
        return newState as RideType[];
    };
    const [displayUtility, setDisplayUtility] = useState<displayUtilityType>(
        ''
    );
    function handleUtility(event: React.MouseEvent<HTMLButtonElement>) {
        const utilityButton = event.currentTarget as HTMLButtonElement;
        const ariaLabel = (utilityButton.getAttribute('aria-label') ||
            utilityButton.ariaLabel) as displayUtilityType;
        setDisplayUtility(prevState => {
            if (prevState === ariaLabel) {
                return '';
            }
            return ariaLabel;
        });
    }
    const [fireEvents] = useAnalytics();
    const triggerLoadAnalytics = () => {
        fireEvents(
            ['target-trigger-view', 'ford-rides-landing'],
            'ford-rides-landing'
        );
    };
    useEffect(() => {
        triggerLoadAnalytics();
        window.scrollTo({
            top: 0,
            behavior:
                'scrollBehavior' in document.documentElement.style
                    ? 'smooth'
                    : 'auto',
        });
    }, []);

    function utilityButtons(displayUtility: displayUtilityType) {
        const exportRideType: RideType[] = [
            'business',
            'personal',
            'unassigned',
        ];
        switch (displayUtility) {
            case 'Filter':
                return (
                    <div className="distance-duration-wrapper">
                        <Suspense fallback={<ActivityIndicator />}>
                            <RidesDistanceDurationFilter />
                        </Suspense>
                    </div>
                );
            case 'Export':
                return <RidesExport rideType={exportRideType} />;
            default:
                return null;
        }
    }

    function getFilterCount() {
        let count = (props.rideType || []).length;
        const distanceFilterKeyValue = {
            distanceFrom: distanceDurationFilter.distanceFrom,
            distanceTo: distanceDurationFilter.distanceTo,
        };
        const durationFilterKeyValue = {
            durationFrom: distanceDurationFilter.durationFrom,
            durationTo: distanceDurationFilter.durationTo,
        };
        if (
            distanceFilterKeyValue.distanceFrom ||
            distanceFilterKeyValue.distanceTo
        ) {
            count++;
        }
        if (
            durationFilterKeyValue.durationFrom ||
            durationFilterKeyValue.durationTo
        ) {
            count++;
        }
        return count;
    }

    return (
        <div className="ford-rides-home">
            {showNotification &&
                Boolean(state['unassigned']?.length) &&
                (fordRidesData.notificationCtaText ||
                    fordRidesData.notificationText) && (
                    <div className="ford-rides-home-notification">
                        <img
                            alt="notification"
                            className="notification-icon"
                            src={notificationIcon}
                        />
                        <span className="notification-content">
                            <div>
                                <span className="notification-title">
                                    {fordRidesData.notificationCtaText}
                                </span>{' '}
                                <SecondaryButton
                                    className="notification-body"
                                    onClick={() => {
                                        props.setView(ComponentType.AllRides);
                                        props.setRideType(['unassigned']);
                                    }}
                                >
                                    {fordRidesData.notificationText}
                                </SecondaryButton>
                            </div>
                        </span>
                        <button
                            className="notification-close"
                            onClick={() => {
                                setShowNotification(false);
                            }}
                        >
                            <img src={close} alt="close" />
                        </button>
                    </div>
                )}
            <h1 className="ford-rides-home-title">
                {fordRidesData.fordRidesHeading}
            </h1>
            <div className="ford-rides-date__placeholder">
                {FordRidesDateRangePicker}
            </div>
            <div className="ford-rides-cards">
                <div className="utility-bar__wrapper">
                    <div className="utility-bar">
                        <PrimaryButton
                            color="dark"
                            fill="outline"
                            chevron={true}
                            disabled={
                                !Object.keys(state).some(stateKey => {
                                    if (
                                        (state[
                                            stateKey as keyof ReducerInitialStates
                                        ] as ReducerInitialState[]).length
                                    ) {
                                        return true;
                                    } else {
                                        return false;
                                    }
                                })
                            }
                            onClick={() => {
                                fireEvents(
                                    ['ford-rides-view-all-cta'],
                                    undefined,
                                    {
                                        fieldName: ComponentType.AllRides,
                                    },
                                    false
                                );
                                props.setView(ComponentType.AllRides);
                                props.setRideType(handleSetRideType as any);
                            }}
                        >
                            {fordRidesData.allRidesCtaText}
                        </PrimaryButton>
                        <UtilityButtons
                            getFilterCount={getFilterCount}
                            handleUtility={handleUtility}
                            filterIcon={filterIcon}
                            exportIcon={exportIcon}
                            state={state}
                        />
                    </div>
                    {utilityButtons(displayUtility)}
                </div>
                {Object.keys(state).map(accordionCategory => {
                    return (
                        <FordRidesAccordions
                            fordRidesData={fordRidesData}
                            key={accordionCategory}
                            state={state}
                            setRideType={props.setRideType}
                            setView={props.setView}
                            apiStatus={apiStatus}
                            accordionCategory={
                                accordionCategory as keyof ReducerInitialStates
                            }
                        />
                    );
                })}
            </div>
        </div>
    );
}

export default FordRidesHome;
